/**
 * 산재보험료 20% 환급 — 공개(무로그인) 절감액 계산기 (별도 페이지 /insurance-refund).
 * 업종·인원·보수 입력 → 예상 절감액 + 3단계 안내 + 가입 CTA. 무LLM(요율 YAML 계산).
 */
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  FiTrendingDown, FiCheckCircle, FiArrowRight, FiShield, FiAward,
  FiChevronDown, FiInfo, FiShare2,
} from 'react-icons/fi'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import Button from '../../components/ui/Button'
import Card, { CardBody } from '../../components/ui/Card'
import Spinner from '../../components/ui/Spinner'
import { cn } from '../../lib/utils'
import { IR_I18N, type IrLang } from './irI18n'
import {
  irMeta, irEstimatePublic, type IrEstimate, type IrMeta,
} from '../../lib/insuranceRefundApi'
import LeadCaptureBox from './LeadCaptureBox'
import { trackView } from '../../lib/trackView'
import { IR_INDUSTRIES } from './irArchive'

const SIGNUP_PATH = '/signup'

function useIrLang(): IrLang {
  const { i18n } = useTranslation()
  const forcedEn = typeof window !== 'undefined' && window.location.pathname.startsWith('/en/')
  return forcedEn || i18n.language?.startsWith('en') ? 'en' : 'ko'
}

const SIGNAL_BAR = ['#3b82f6', '#22c55e', '#f59e0b', '#a78bfa']

export default function InsuranceRefund() {
  const lang = useIrLang()
  const s = IR_I18N[lang]

  const metaQ = useQuery({ queryKey: ['ir-meta'], queryFn: irMeta })
  const meta = metaQ.data

  const [industryKey, setIndustryKey] = useState('')
  const [workers, setWorkers] = useState<string>('')
  const [wage, setWage] = useState<string>('')
  const [result, setResult] = useState<IrEstimate | null>(null)
  const [loading, setLoading] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(0)
  const [copied, setCopied] = useState(false)

  // 유입 귀속(h_src 쿠키) — 이 계산기로 들어와 가입하면 출처가 'insurance-refund'로 잡힌다.
  // 이게 없으면 아무리 뿌려도 채널 효과 측정·전환 추적이 안 된다(고도화 1층).
  useEffect(() => { trackView('insurance-refund', 'calc') }, [])

  // 선택 업종 → 업종 랜딩 slug(프리렌더 OG 보유). 공유·교차링크 대상.
  const matchedSlug = IR_INDUSTRIES.find((i) => i.key === industryKey)?.slug

  // meta 로드 시 보수 기본값 프리필
  const wageNum = wage === '' ? (meta?.default_monthly_wage ?? 0) : Number(wage)

  const canCalc = !!industryKey && Number(workers) >= 1 && Number(workers) < 50 && wageNum > 0

  async function onCalc() {
    if (!canCalc) return
    setLoading(true)
    try {
      const r = await irEstimatePublic({
        industry_key: industryKey,
        worker_count: Number(workers),
        monthly_wage_per_capita: wageNum,
      })
      setResult(r)
      setTimeout(() => document.getElementById('ir-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
    } catch {
      /* 계산 실패는 조용히 무시(폼 유지) */
    } finally {
      setLoading(false)
    }
  }

  // 결과 공유(고도화 2층) — 모바일은 네이티브 공유시트(카톡 포함), 데스크톱은 링크 복사.
  // 공유 URL 은 업종 랜딩이라 카톡/카페 미리보기 카드가 업종별로 뜬다(동적 숫자 미리보기는
  // 서버 OG 가 필요 → 후순위). 자영업 커뮤니티에서 계산기가 퍼지는 유일한 통로.
  async function shareResult() {
    if (!result) return
    const base = typeof window !== 'undefined' ? window.location.origin : ''
    const url = `${base}/insurance-refund${matchedSlug ? '/' + matchedSlug + '/' : ''}`
    const text = lang === 'en'
      ? `Our workplace could cut ${s.won(result.headline_savings)} in industrial-accident insurance premiums. Free 30-sec calculator 👇`
      : `우리 사업장, 산재보험료를 연 ${s.won(result.headline_savings)} 아낄 수 있대요! 30초 무료 계산 👇`
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: 'Heinrich 산재보험료 환급 계산기', text, url })
        return
      }
    } catch {
      return /* 사용자가 공유 취소 */
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard 불가 환경 무시 */
    }
  }

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>{s.metaTitle}</title>
        <meta name="description" content={s.metaDesc} />
        <link rel="canonical" href="https://heinrich.dasancms.co.kr/insurance-refund/" />
        <meta property="og:title" content={s.metaTitle} />
        <meta property="og:description" content={s.metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:py-14">
        {/* HERO */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/12 px-3 py-1 text-xs font-semibold text-blue-300">
            <FiTrendingDown size={13} /> {s.badge}
          </span>
          <h1 className="mt-4 text-3xl font-bold leading-tight text-ink sm:text-4xl">
            {s.heroTitle}{' '}
            <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
              {s.heroHighlight}
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-ink-muted">
            {s.heroSubtitle}
          </p>
        </motion.div>

        {/* CALCULATOR */}
        <Card className="mt-8">
          <CardBody className="p-6 sm:p-8">
            <h2 className="flex items-center gap-2 text-lg font-bold text-ink">
              <FiAward className="text-blue-400" /> {s.calcTitle}
            </h2>
            {metaQ.isLoading ? (
              <div className="flex justify-center py-10"><Spinner /></div>
            ) : (
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink-muted">{s.industryLabel}</span>
                  <select value={industryKey} onChange={(e) => setIndustryKey(e.target.value)}
                    className="w-full rounded-lg border border-surface-elevated/60 bg-surface-card px-3 py-2.5 text-sm text-ink focus:border-blue-400 focus:outline-none">
                    <option value="">{s.industryPlaceholder}</option>
                    {(meta?.industries ?? []).map((it) => (
                      <option key={it.key} value={it.key}>{it.label} ({it.rate_pct}%)</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink-muted">{s.workerLabel}</span>
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} max={49} value={workers} placeholder="20"
                      onChange={(e) => setWorkers(e.target.value)}
                      className="w-full rounded-lg border border-surface-elevated/60 bg-surface-card px-3 py-2.5 text-sm text-ink focus:border-blue-400 focus:outline-none" />
                    {s.workerUnit && <span className="text-sm text-ink-muted">{s.workerUnit}</span>}
                  </div>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink-muted">{s.wageLabel}</span>
                  <div className="relative">
                    <input type="text" inputMode="numeric"
                      value={wage ? Number(wage).toLocaleString('ko-KR') : ''}
                      placeholder={Number(meta?.default_monthly_wage ?? 3000000).toLocaleString('ko-KR')}
                      onChange={(e) => setWage(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-full rounded-lg border border-surface-elevated/60 bg-surface-card px-3 py-2.5 pr-8 text-sm text-ink focus:border-blue-400 focus:outline-none" />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-faint">{lang === 'en' ? '' : '원'}</span>
                  </div>
                  <span className="mt-1 block text-[11px] leading-snug text-ink-faint">{s.wageHint}</span>
                </label>
              </div>
            )}
            {Number(workers) >= 50 && (
              <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-[13px] text-amber-300">
                {lang === 'en'
                  ? 'The prevention-rate program applies to workplaces with fewer than 50 workers.'
                  : '산재예방요율제는 상시근로자 50인 미만 사업장이 대상입니다. (50인 이상은 해당하지 않아요)'}
              </p>
            )}
            <Button onClick={onCalc} disabled={!canCalc || loading} className="mt-5 w-full sm:w-auto">
              {loading ? s.calculating : s.calcBtn}
            </Button>
          </CardBody>
        </Card>

        {/* RESULT */}
        {result && (
          <motion.div id="ir-result" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-6">
            <Card className="border-blue-500/30">
              <CardBody className="p-6 sm:p-8">
                <div className="flex items-center gap-2 text-emerald-300">
                  <FiCheckCircle /> <span className="font-bold">{s.eligibleTitle}</span>
                </div>
                <p className="mt-1 text-sm text-ink-muted">{s.eligibleBody(result.industry_label)}</p>

                <div className="mt-5 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10 p-6 text-center">
                  <div className="text-xs text-ink-muted">{s.savingsLead}</div>
                  <div className="mt-1 bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-4xl font-extrabold text-transparent sm:text-5xl">
                    {s.won(result.headline_savings)}
                  </div>
                  <div className="mt-1 text-[13px] text-ink-faint">
                    {s.premiumNote(s.won(result.annual_premium))}
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-blue-500/25 bg-blue-500/[0.05] p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-blue-300">
                      <FiShield size={15} /> {s.trackRaTitle}
                    </div>
                    <div className="mt-1 text-2xl font-bold text-ink">
                      {s.won(result.tracks.risk_assessment.savings_total)}
                    </div>
                    <div className="text-[12px] text-ink-muted">{s.trackRaDesc}</div>
                  </div>
                  <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05] p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-300">
                      <FiAward size={15} /> {s.trackEduTitle}
                    </div>
                    <div className="mt-1 text-2xl font-bold text-ink">
                      {s.won(result.tracks.owner_education.savings_total)}
                    </div>
                    <div className="text-[12px] text-ink-muted">{s.trackEduDesc}</div>
                  </div>
                </div>

                {/* CTA (결과 직후 전환 유도) — 결과 숫자에 직접 붙인 훅 + 공유 */}
                <div className="mt-6 flex flex-col items-center gap-3 rounded-xl bg-surface-elevated/30 p-5 text-center">
                  <div className="text-base font-bold text-ink">{s.ctaTitle}</div>
                  <p className="max-w-xl text-sm text-ink-muted">
                    {lang === 'en' ? (
                      <>To actually claim this <b className="text-emerald-300">{s.won(result.headline_savings)}</b>, you need risk-assessment evidence — Heinrich generates it for free.</>
                    ) : (
                      <>이 <b className="text-emerald-300">{s.won(result.headline_savings)}</b>을 실제로 돌려받으려면 <b className="text-ink">위험성평가 증빙</b>이 필요해요. Heinrich가 무료로 만들어 드립니다.</>
                    )}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Link to={SIGNUP_PATH}><Button>{s.ctaBtn} <FiArrowRight className="ml-1.5" /></Button></Link>
                    <Button variant="secondary" onClick={shareResult} leftIcon={<FiShare2 />}>
                      {copied ? (lang === 'en' ? 'Copied ✓' : '복사됨 ✓') : (lang === 'en' ? 'Share result' : '결과 공유하기')}
                    </Button>
                  </div>
                  {matchedSlug && (
                    <Link to={`/insurance-refund/${matchedSlug}`} className="text-[12px] text-blue-300 hover:underline">
                      {lang === 'en' ? `More on ${result.industry_label} refunds →` : `${result.industry_label} 산재환급 더 알아보기 →`}
                    </Link>
                  )}
                </div>

                <div className="mt-4">
                  <LeadCaptureBox lang={lang} source="insurance-refund" />
                </div>

                {result.legal_notice && (
                  <p className="mt-4 text-[11px] leading-relaxed text-ink-faint">
                    <FiInfo className="mr-1 inline" size={11} />{result.legal_notice[lang]}
                  </p>
                )}
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* HOW-TO */}
        <section className="mt-12">
          <h2 className="text-center text-xl font-bold text-ink">{s.howTitle}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {s.howSteps.map((st, i) => (
              <div key={i} className="rounded-xl border border-surface-elevated/40 bg-surface-card p-5">
                <div className="text-sm font-bold text-blue-300">{st.t}</div>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">{st.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CRITERIA */}
        <section className="mt-10 rounded-xl border border-surface-elevated/40 bg-surface-card p-6">
          <h2 className="text-lg font-bold text-ink">{s.criteriaTitle}</h2>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">{s.criteriaLead}</p>
          <div className="mt-4 space-y-2">
            {(meta?.review_criteria ?? []).map((c, i) => (
              <div key={c.key} className="flex items-center gap-3">
                <div className="w-28 shrink-0 text-[13px] text-ink-muted">
                  {lang === 'en' ? c.title_en : c.title_ko}
                </div>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-elevated/40">
                  <div className="h-full rounded-full" style={{ width: `${c.weight}%`, background: SIGNAL_BAR[i % SIGNAL_BAR.length] }} />
                </div>
                <div className="w-10 text-right text-[13px] font-semibold text-ink">{c.weight}%</div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-10">
          <h2 className="text-center text-xl font-bold text-ink">{s.faqTitle}</h2>
          <div className="mt-5 space-y-2">
            {s.faq.map((f, i) => (
              <div key={i} className="overflow-hidden rounded-lg border border-surface-elevated/40 bg-surface-card">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-ink">
                  {f.q}
                  <FiChevronDown className={cn('shrink-0 transition-transform', openFaq === i && 'rotate-180')} />
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 text-[13px] leading-relaxed text-ink-muted">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 하단 CTA */}
        <div className="mt-12 flex flex-col items-center gap-3 rounded-2xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10 p-8 text-center">
          <div className="text-xl font-bold text-ink">{s.ctaTitle}</div>
          <Link to={SIGNUP_PATH}><Button size="lg">{s.ctaBtn} <FiArrowRight className="ml-1.5" /></Button></Link>
        </div>
      </div>
    </>
  )
}
